import React, { useState, useEffect } from 'react';
import { FaCheck, FaHome } from 'react-icons/fa';
import { useAuth } from '../../AuthContext';
import { useNavigate } from 'react-router-dom';

// Import step components
import ServiceLocationStep from './steps/ServiceLocationStep';
import WasteInformationStep, { wasteTypes } from './steps/WasteInformationStep';
import BinRequirementsStep, { binSizes } from './steps/BinRequirementsStep';
import DeliveryAndPickupStep from './steps/DeliveryAndPickupStep';
import ContactInfoStep from './steps/ContactInfoStep';
import DataSummary from '../DataSummary';
import PreviewSubmitStep from './steps/PreviewSubmitStep';

const steps = [
  { 
    title: "Service Location",
    component: ServiceLocationStep,
    fields: ['streetAddress', 'city', 'postalCode']
  },
  { 
    title: "Waste Information",
    component: WasteInformationStep,
    fields: ['wasteTypeId', 'isHazardous', 'hazardousMaterials']
  },
  { 
    title: "Bin Requirements",
    component: BinRequirementsStep,
    fields: ['binSizeId']
  },
  { 
    title: "Delivery & Pickup",
    component: DeliveryAndPickupStep,
    fields: ['rentalStartDate', 'rentalDuration', 'rentalEndDate', 'deliveryInstructions']
  },
  { 
    title: "Contact Information",
    component: ContactInfoStep,
    fields: ['email', 'fullName', 'mobileNumber', 'homeNumber', 'businessName', 'secondContactName', 'secondContactMobile', 'secondContactEmail']
  },
  {
    title: "Preview & Submit",
    component: PreviewSubmitStep,
    fields: []
  }
];

const SkipBinWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Service Location
    streetAddress: '',
    city: '',
    postalCode: '',
    // Waste Information
    wasteTypeId: '',
    wasteType: '',
    isHazardous: false,
    hazardousMaterials: '',
    // Bin Requirements
    binSizeId: '',
    binSize: '',
    // Delivery & Pickup
    rentalStartDate: '',
    rentalDuration: '',
    rentalEndDate: '',
    deliveryInstructions: '',
    // Contact Info
    email: '',
    fullName: '',
    mobileNumber: '',
    homeNumber: '',
    businessName: '',
    secondContactName: '',
    secondContactMobile: '',
    secondContactEmail: '',
    companyId: 'INAB',
  });
  const [errors, setErrors] = useState({});
  const { login, user } = useAuth();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    if (formData.rentalStartDate && formData.rentalDuration) {
      const startDate = new Date(formData.rentalStartDate);
      const endDate = new Date(startDate.setDate(startDate.getDate() + parseInt(formData.rentalDuration)));
      setFormData(prevData => ({
        ...prevData,
        rentalEndDate: endDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.rentalStartDate, formData.rentalDuration]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'binSizeId') {
      const selectedBin = binSizes.find(bin => bin.id === value);
      setFormData(prevData => ({
        ...prevData,
        binSizeId: value,
        binSize: selectedBin ? selectedBin.label : ''
      }));
    } else if (name === 'wasteTypeId') {
      const selectedWaste = wasteTypes.find(waste => waste.id === value);
      setFormData(prevData => ({
        ...prevData,
        wasteTypeId: value,
        wasteType: selectedWaste ? selectedWaste.label : ''
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    if (errors[name]) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: null }));
    }
  };

  const updateFormData = (newFormData) => {
    setFormData(newFormData);
  };

  const validateStep = () => {
    const newErrors = {};
    const CurrentStepComponent = steps[currentStep].component;
    
    const requiredFields = {
      ServiceLocationStep: ['streetAddress', 'city', 'postalCode'],
      WasteInformationStep: ['wasteTypeId'],
      BinRequirementsStep: ['binSizeId'],
      DeliveryPickupStep: ['rentalStartDate', 'rentalDuration'],
      ContactInfoStep: ['email', 'fullName', 'mobileNumber'],
      PreviewSubmitStep: []
    };
  
    const stepName = CurrentStepComponent.name;
    const fieldsToValidate = requiredFields[stepName] || [];
  
    fieldsToValidate.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
  
    if (stepName === 'WasteInformationStep' && formData.isHazardous && !formData.hazardousMaterials) {
      newErrors.hazardousMaterials = 'Please describe the hazardous materials';
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prevStep => prevStep + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prevStep => prevStep - 1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep()) {
      console.log('Form submitted:', formData);
    }
  };

  const handleSocialLogin = (provider) => {
    console.log(`Logging in with ${provider}`);
  };

  const verifyEmail = (email) => {
    console.log(`Verifying email: ${email}`);
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={index}>
          <div className={`flex flex-col items-center ${index <= currentStep ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`rounded-full transition duration-500 ease-in-out h-12 w-12 py-3 border-2 flex items-center justify-center ${
              index < currentStep ? 'bg-green-600 border-green-600' : 
              index === currentStep ? 'border-green-600' : 'border-gray-300'
            }`}>
              {index < currentStep ? (
                <FaCheck className="text-white" />
              ) : (
                <span className={index === currentStep ? 'text-green-600' : 'text-gray-500'}>{index + 1}</span>
              )}
            </div>
            <p className="text-xs mt-2">{step.title}</p>
          </div>
          {index < steps.length - 1 && (
            <div className={`flex-auto border-t-2 transition duration-500 ease-in-out ${
              index < currentStep ? 'border-green-600' : 'border-gray-300'
            }`}></div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const CurrentStepComponent = steps[currentStep].component;

  const commonProps = {
    formData,
    handleInputChange,
    errors,
    user,
    handleSocialLogin,
    verifyEmail,
    login,
  };

  const stepProps = CurrentStepComponent === ServiceLocationStep
    ? { ...commonProps, updateFormData }
    : commonProps;

  const showNextButton = () => {
    if (CurrentStepComponent === ContactInfoStep && !user) {
      return false;
    }
    return currentStep < steps.length - 1;
  };

  const handleHomeNavigation = () => {
    setShowConfirmDialog(true);
  };

  const confirmHomeNavigation = () => {
    setShowConfirmDialog(false);
    navigate('/');
  };

  const ConfirmationDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
        <h2 className="text-xl font-bold mb-4">Confirm Navigation</h2>
        <p className="mb-6">Navigating away from this page will result in losing all entered Bin Request data. Are you sure you want to proceed?</p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={() => setShowConfirmDialog(false)}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmHomeNavigation}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6 bg-white rounded-lg shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-green-600">Skip Bin Requirements</h1>
        <button
          onClick={handleHomeNavigation}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors flex items-center"
        >
          <FaHome className="mr-2" /> Home
        </button>
      </div>
      {showConfirmDialog && <ConfirmationDialog />}
      <StepIndicator />
      <div className="flex flex-col md:flex-row md:space-x-6">
        <div className="flex-grow mb-6 md:mb-0">
          <form onSubmit={handleSubmit}>
            <CurrentStepComponent {...stepProps} />
            <div className="mt-8 flex justify-between">
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition-colors"
                >
                  Previous
                </button>
              )}
              {showNextButton() ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                >
                  Next
                </button>
              ) : currentStep === steps.length - 1 && (
                <button
                  type="submit"
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 transition-colors"
                >
                  Submit
                </button>
              )}
            </div>
          </form>
        </div>
        <div className="w-full md:w-1/3">
          <DataSummary formData={formData} steps={steps} />
        </div>
      </div>
    </div>
  );
};

export default SkipBinWizard;